import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';

// store
import { useStoreValue, storeSet } from '../../store';

// components
import StudioTimeline from './components/StudioTimeline';
import RecSessionTimeline from './components/RecSessionTimeline';
import TimelineControlsWidget from '../../components/TimelineControlsWidget';
import MyStudioLeftPanel from './components/MyStudioLeftPanel';
import PinIcon from '../../icons/PinIcon';
import ButtonIconRadioFlat from '../../components/ButtonWrapper/components/ButtonIconRadioFlat';
import WizardIcon from '../../icons/WizardIcon';
import RollingArrowsIcon from '../../icons/RollingArrowsIcon';
import PatchIcon from '../../icons/PatchIcon';
import PenAiIcon from '../../icons/PenAiIcon';
import PinAiIcon from '../../icons/PinAiIcon';
import MoodAiIcon from '../../icons/MoodAiIcon';
import Tooltip from '../../components/Tooltip';
import CommCenterMyStudio from './components/CommCenterMyStudio';
import ModalStemSplitter from '../../components/ModalStemSplitter';
import AiNotePopup from '../../components/AiNotePopup';
import useCacheProjectAudio from '../../hooks/useCacheProjectAudio';

const ProjectMyStudio = () => {
    const { projectId } = useParams();
    const selectedVideo = useStoreValue('selectedVideo');
    const myStudioLeftPanel = useStoreValue('myStudioLeftPanel');
    const stemSplitterSelection = useStoreValue('stemSplitterSelection');
    const generateInstrumentSelection = useStoreValue('generateInstrumentSelection');
    const aiNoteSelection = useStoreValue('aiNoteSelection');
    const aiNoteSubmissions = useStoreValue('aiNoteSubmissions');
    const penCanvasData = useStoreValue('penCanvasData');
    const userData = useStoreValue('userData');
    const lastProjectId = useRef(null);
    const prevSelectedVideoRef = useRef(null);

    // Button group state - only one can be selected at a time
    const [aiEditTool, setAiEditTool] = useState(null);

    const AI_NOTE_MODES = ['penai', 'pinai', 'moodai'];

    const handleButtonToggle = useCallback((value) => {
        const next = aiEditTool === value ? null : value;
        setAiEditTool(next);
        if (value === 'stemSplitter') {
            storeSet('timelineEditingMode', next === 'stemSplitter' ? 'stemSplitter' : '');
        }
        if (value === 'generateInstrument') {
            storeSet('timelineEditingMode', next === 'generateInstrument' ? 'generateInstrument' : '');
        }
        if (AI_NOTE_MODES.includes(value)) {
            storeSet('timelineEditingMode', next ? value : '');
            if (!next) storeSet('aiNoteSelection', null);
        }
    }, [aiEditTool]);

    const handleResetPenCanvas = useCallback(() => {
        storeSet('penCanvasResetSignal', Date.now());
    }, []);

    const handleAiNoteSend = useCallback(async (note, selection, mood) => {
        const now = new Date().toISOString();
        const tempId = `local_${Date.now()}`;

        const NOTE_TYPE_MAP = { penai: 'pen', pinai: 'pin', moodai: 'mood' };
        const noteType = NOTE_TYPE_MAP[aiEditTool];

        let extra = { noteType };
        if (aiEditTool === 'penai') {
            extra = {
                ...extra,
                canvasData: JSON.stringify(penCanvasData?.json ?? null),
                layer: selection?.layer ?? null,
                startTimecode: selection?.startPos ?? null,
                endTimecode: selection?.endPos ?? null,
            };
        } else if (aiEditTool === 'pinai') {
            extra = {
                ...extra,
                layer: selection?.layer ?? null,
                startTimecode: selection?.startPos ?? null,
                endTimecode: selection?.endPos ?? null,
            };
        } else if (aiEditTool === 'moodai') {
            extra = {
                ...extra,
                layer: selection?.layer ?? null,
                startTimecode: selection?.startPos ?? null,
                endTimecode: selection?.endPos ?? null,
                mood: mood ?? null,
            };
        }

        storeSet('aiNoteSubmissions', [...(aiNoteSubmissions || []), {
            id: tempId,
            mode: aiEditTool,
            note,
            mood: mood || null,
            layer: selection?.layer,
            startPos: selection?.startPos,
            endPos: selection?.endPos,
            timelineType: selection?.timelineType,
            createdAt: now,
        }]);

        if (aiEditTool === 'penai') {
            storeSet('penCanvasResetSignal', Date.now());
        }

        storeSet('aiNoteSelection', null);
        storeSet('timelineEditingMode', '');
        setAiEditTool(null);

        try {
            const created = await storeSet('sendMessage', projectId, { body: note, extra }, 'TEAM');
            if (created?.id) {
                storeSet('aiNoteSubmissions', (aiNoteSubmissions || []).map(s =>
                    s.id === tempId ? { ...s, id: created.id } : s
                ));
            }
        } catch {
            // submission overlay remains visible for the session
        }
    }, [aiEditTool, aiNoteSubmissions, userData, penCanvasData, projectId]);

    useEffect(() => {
        if (lastProjectId.current !== projectId) {
            if (lastProjectId.current) {
                storeSet('cleanupVideoCache', lastProjectId.current, null);
            }
            lastProjectId.current = projectId;
            storeSet('refetchCueSheet', projectId);
        }
    }, [projectId]);

    useCacheProjectAudio(projectId);

    // Handle selectedVideo changes - cache selected video
    useEffect(() => {
        if (prevSelectedVideoRef.current !== selectedVideo) {
            if (prevSelectedVideoRef.current !== null && prevSelectedVideoRef.current !== 0) {
                storeSet('cleanupVideoCache', projectId, prevSelectedVideoRef.current);
            }
            if (selectedVideo && selectedVideo !== 0) {
                storeSet('cacheSelectedVideo', projectId);
            }
            prevSelectedVideoRef.current = selectedVideo;
        }
    }, [selectedVideo, projectId]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (projectId) {
                storeSet('cleanupAudioCache', projectId);
            }
        };
    }, [projectId]);

    return (
        <>
            <div className="appDashboard appMyStudio">
                <div className="appDashboard__workbench timelineEditorAuxWorkbench">
                    <div className="appWorkbenchPanel">
                        <MyStudioLeftPanel/>
                    </div>
                </div>
                <div className="appDashboard__workbench timelineEditorWorkbench">
                    {myStudioLeftPanel !== 'rec' ? <StudioTimeline key={projectId} /> : <RecSessionTimeline />}
                </div>
                <CommCenterMyStudio/>
            </div>
            <div className="appDashboard__widgets appMyStudio__widgets">
                <div className="appWorkbenchPanel">
                    <div>
                        <Tooltip label="Stem Splitter">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('stemSplitter')}
                                value="stemSplitter"
                                isSquare={true}
                                checked={aiEditTool === 'stemSplitter'}>
                                <PinIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                        <Tooltip label="Generate Instrument">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('generateInstrument')}
                                value="generateInstrument"
                                isSquare={true}
                                checked={aiEditTool === 'generateInstrument'}
                                disabled={myStudioLeftPanel !== 'rec'}>
                                <WizardIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                        <Tooltip label="Rolling">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('rolling')}
                                value="rolling"
                                isSquare={true}
                                checked={aiEditTool === 'rolling'}>
                                <RollingArrowsIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                        <Tooltip label="Patch">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('patch')}
                                value="patch"
                                isSquare={true}
                                checked={aiEditTool === 'patch'}>
                                <PatchIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                    </div>

                    <TimelineControlsWidget />

                    <div className="aiNotePopup__anchor">
                        <Tooltip label="Pen">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('penai')}
                                value="penai"
                                isSquare={true}
                                color="var(--note)"
                                checked={aiEditTool === 'penai'}>
                                <PenAiIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                        <Tooltip label="Pin">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('pinai')}
                                value="pinai"
                                isSquare={true}
                                color="var(--moodcolor01)"
                                checked={aiEditTool === 'pinai'}>
                                <PinAiIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                        <Tooltip label="Mood">
                            <ButtonIconRadioFlat
                                onChange={() => handleButtonToggle('moodai')}
                                value="moodai"
                                isSquare={true}
                                color="var(--accent)"
                                checked={aiEditTool === 'moodai'}>
                                <MoodAiIcon/>
                            </ButtonIconRadioFlat>
                        </Tooltip>
                        {aiEditTool === 'penai' && (
                            <AiNotePopup
                                mode="penai"
                                icon={<PenAiIcon />}
                                color="var(--note)"
                                selection={aiNoteSelection}
                                onSend={handleAiNoteSend}
                                onReset={handleResetPenCanvas}
                                onClose={() => handleButtonToggle('penai')}
                            />
                        )}
                        {aiEditTool === 'pinai' && (
                            <AiNotePopup
                                mode="pinai"
                                icon={<PinAiIcon />}
                                color="var(--moodcolor01)"
                                selection={aiNoteSelection}
                                onSend={handleAiNoteSend}
                                onClose={() => handleButtonToggle('pinai')}
                            />
                        )}
                        {aiEditTool === 'moodai' && (
                            <AiNotePopup
                                mode="moodai"
                                icon={<MoodAiIcon />}
                                color="var(--accent)"
                                selection={aiNoteSelection}
                                onSend={handleAiNoteSend}
                                onClose={() => handleButtonToggle('moodai')}
                            />
                        )}
                    </div>
                </div>
            </div>
            <ModalStemSplitter
                visible={!!stemSplitterSelection}
                selection={stemSplitterSelection}
                onHide={() => {
                    storeSet('stemSplitterSelection', null);
                    storeSet('timelineEditingMode', '');
                    setAiEditTool(null);
                }}
                onSubmit={(data) => {
                    console.log('Stem Splitter submit:', data);
                    storeSet('stemSplitterSelection', null);
                    storeSet('timelineEditingMode', '');
                    setAiEditTool(null);
                }}
            />
            <ModalStemSplitter
                title="Genera strumento"
                visible={!!generateInstrumentSelection}
                selection={generateInstrumentSelection}
                onHide={() => {
                    storeSet('generateInstrumentSelection', null);
                    storeSet('timelineEditingMode', '');
                    setAiEditTool(null);
                }}
                onSubmit={(data) => {
                    console.log('Generate Instrument submit:', data);
                    storeSet('generateInstrumentSelection', null);
                    storeSet('timelineEditingMode', '');
                    setAiEditTool(null);
                }}
            />
        </>
    )
}

export default ProjectMyStudio;